import React from 'react';
import { Box, Chip, Popover } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';

import MatchModePicker from '../Watchlists/parts/MatchModePicker';
import type { WatchlistMatchMode } from '../../types';

const HOVER_DELAY_MS = 1000;
const LONG_PRESS_MS = 550;

type PropsType = {
    label: string,
    selected: boolean,
    mode: WatchlistMatchMode,
    onSelect: () => void,
    onModeChange: (mode: WatchlistMatchMode) => void,
    chipProps?: Partial<ChipProps>
};

/** Route filter chip; when selected, long hover/press, ArrowDown or its arrow icon opens the match-mode popover. */
export default function RouteModeChip({
    label, selected, mode, onSelect, onModeChange, chipProps
}: PropsType) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const chipRef = React.useRef<HTMLDivElement | null>(null);
    const timer = React.useRef<number | undefined>();
    const longPressed = React.useRef(false);
    const hovering = React.useRef(false);

    const cancelTimer = React.useCallback(() => {
        window.clearTimeout(timer.current);
        timer.current = undefined;
    }, []);
    const open = React.useCallback(() => setAnchor(chipRef.current), []);
    const startTimer = (ms: number) => {
        cancelTimer();
        if (!selected) return;
        timer.current = window.setTimeout(() => {
            longPressed.current = ms === LONG_PRESS_MS;
            open();
        }, ms);
    };
    React.useEffect(() => cancelTimer, [cancelTimer]);

    // Only the selected chip offers the mode choice; start the hover timer when it gets selected under the mouse
    React.useEffect(() => {
        cancelTimer();
        if (!selected) {
            setAnchor(null);
            return;
        }
        if (hovering.current) {
            timer.current = window.setTimeout(open, HOVER_DELAY_MS);
        }
    }, [selected, cancelTimer, open]);

    const modeText = selected && mode === 'within' ? ' · Helt inom' : '';

    return (
        <>
            <Chip
                ref={chipRef}
                size="small"
                label={`${label}${modeText}`}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => {
                    if (longPressed.current) {
                        longPressed.current = false;
                        return;
                    }
                    cancelTimer();
                    onSelect();
                }}
                {...(selected ? {
                    onDelete: open,
                    deleteIcon: <ArrowDropDownIcon aria-label="Välj vilka uppdrag som visas" />
                } : {})}
                onKeyDown={(e) => {
                    if (selected && e.key === 'ArrowDown') {
                        e.preventDefault();
                        open();
                    }
                }}
                onPointerEnter={(e) => {
                    if (e.pointerType !== 'mouse') return;
                    hovering.current = true;
                    startTimer(HOVER_DELAY_MS);
                }}
                onPointerLeave={() => {
                    hovering.current = false;
                    cancelTimer();
                }}
                onTouchStart={() => { longPressed.current = false; startTimer(LONG_PRESS_MS); }}
                onTouchEnd={cancelTimer}
                onTouchMove={cancelTimer}
                onTouchCancel={cancelTimer}
                onContextMenu={(e) => { if (longPressed.current) e.preventDefault(); }}
                aria-haspopup={selected ? 'dialog' : undefined}
                {...chipProps}
            />
            <Popover
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Box sx={{ p: 1, width: 300, maxWidth: '90vw' }}>
                    <MatchModePicker
                        compact
                        label={`Vilka uppdrag ska visas för ${label}`}
                        value={mode}
                        onChange={(newMode) => {
                            onModeChange(newMode);
                            setAnchor(null);
                        }}
                    />
                </Box>
            </Popover>
        </>
    );
}
